import { types as t, SnapshotOut } from 'mobx-state-tree';
import { persist } from 'mobx-state-tree-persist';
import { ReportAirQualityStandardsTypes } from '~/mst/models/report/types';

import NodesStore, { INodesStore } from '~/mst/models/nodes';
import AlertsStore, { IAlertsStore } from '~/mst/models/alerts';
import OrganizationsStore, { IOrganizationsStore } from '~/mst/models/organizations';
import PartnershipsStore, { IPartnershipsStore } from '~/mst/models/partnerships';
import CertificatesStore, { ICertificatesStore } from '~/mst/models/certificates';
import TemplatesStore, { ITemplatesStore } from '~/mst/models/templates';
import UsersStore, { IUsersStore } from '~/mst/models/users';
import WebhooksStore, { IWebhooksStore } from '~/mst/models/webhooks';
import TokensStore, { ITokensStore } from '~/mst/models/tokens';
import InvitationsStore, { IInvitationsStore } from '~/mst/models/invitations';
import VersionStore, { IVersionStore } from '~/mst/models/version';
import DashboardsStore, { IDashboardsStore } from '~/mst/models/dashboards';
import ReportsStore, { IReportsStore } from '~/mst/models/reports';
import CalibrationsStore, { ICalibrationsStore } from '~/mst/models/calibrations';
import ArchivesStore, { IArchivesStore } from '~/mst/models/archives';
import AuthStore, { IAuthStore } from '~/mst/models/auth';
import StatsStore, { IStatsStore } from '~/mst/models/stats';
import ReferenceDevicesStore, { IReferenceDevicesStore } from '~/mst/models/reference_devices';
import DeviceProfilesStore, { IDevicePofilesStore } from '~/mst/models/device_profiles';
import NoesisStore, { INoesisStore } from '~/mst/models/noesis';
import HomeStore, { IHomeStore } from '~/pages/home/store';
import DeviceMonitorStore, { IDeviceMonitor } from '~/pages/device_monitor/store';

export const RootStore = t.model({
  nodes: t.optional(NodesStore, []),
  alerts: t.optional(AlertsStore, []),
  certificates: t.optional(CertificatesStore, []),
  templates: t.optional(TemplatesStore, []),
  organizations: t.optional(OrganizationsStore, []),
  partnerships: t.optional(PartnershipsStore, []),
  users: t.optional(UsersStore, []),
  webhooks: t.optional(WebhooksStore, []),
  tokens: t.optional(TokensStore, []),
  dashboards: t.optional(DashboardsStore, []),
  reports: t.optional(ReportsStore, []),
  invitations: t.optional(InvitationsStore, []),
  calibrations: t.optional(CalibrationsStore, []),
  archives: t.optional(ArchivesStore, []),
  referenceDevices: t.optional(ReferenceDevicesStore, []),
  version: t.optional(VersionStore, {}),
  stats: t.optional(StatsStore, {}),
  auth: t.optional(AuthStore, {}),
  home: t.optional(HomeStore, {
    widgets: [
      {
        id: 'devicesOnlineChartWidgetId',
        type: 'DevicesOnlineChartWidget'
      },
      {
        id: 'totalDevicesOnlinePieChartWidgetId',
        type: 'TotalDevicesOnlinePieChartWidget',
        chart_type: 'pie_chart'
      },
      {
        id: 'totalDevicesOnlineColumnChartWidgetId',
        type: 'TotalDevicesOnlineColumnChartWidget',
        chart_type: 'column_chart'
      },
      {
        id: 'recentAlertsWidgetId',
        type: 'RecentAlertsWidget'
      }
    ]
  }),
  deviceMonitor: t.optional(DeviceMonitorStore, {
    range: {
      from: { hours: 3 },
      is_pinned: false
    },
    is_updates_on: true,
    selected_tab: 'chart',
    params: {
      selected_params_chart: [],
      selected_params_table: [],
      selected_params_wind_chart: []
    },
    widgets: []
  }),
  deviceProfiles: t.optional(DeviceProfilesStore, [
    {
      id: ReportAirQualityStandardsTypes.air_quality_custom
    }
  ]),
  noesis: t.optional(NoesisStore, {})
});

export interface IRootStore {
  nodes: INodesStore;
  alerts: IAlertsStore;
  certificates: ICertificatesStore;
  templates: ITemplatesStore;
  organizations: IOrganizationsStore;
  partnerships: IPartnershipsStore;
  users: IUsersStore;
  webhooks: IWebhooksStore;
  tokens: ITokensStore;
  dashboards: IDashboardsStore;
  reports: IReportsStore;
  invitations: IInvitationsStore;
  calibrations: ICalibrationsStore;
  archives: IArchivesStore;
  referenceDevices: IReferenceDevicesStore;
  version: IVersionStore;
  home: IHomeStore;
  auth: IAuthStore;
  stats: IStatsStore;
  noesis: INoesisStore;
  deviceMonitor: IDeviceMonitor;
  deviceProfiles: IDevicePofilesStore;
}
export interface IRootStoreSnapshotOut extends SnapshotOut<typeof RootStore> {}

const rootStore = RootStore.create();
export const persistedStore = persist([[rootStore.auth, { key: 'auth', storage: localStorage, whitelist: ['theme', 'organization_id', 'locale'] }]]);

export default rootStore;
